import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { ToastProvider } from './hooks/useToastProvider';
import { applyBrandTheme } from './config/brands/applyBrandTheme';
// lightmedia-admin-theme (社内共通管理画面テーマ)。tokens → components → bridge の順で
// 他の CSS より先に import する。bridge は .admin-layout スコープなので
// /admin/* 以外の通常ユーザー向けダーク UI には影響しない。
import './styles/theme/tokens.css';
import './styles/theme/components.css';
import './styles/theme/image-generator-bridge.css';
import './styles/buttons.css';
import './styles/admin-pages.css';
import './styles/modal-common.css';
import './styles/info-tables.css';
import './App.css';

/**
 * URL からトークン類を除去する。
 *
 * 2026-07-29 監査 CR-1: セッション JWT が URL に載る経路 (M-8 / M-9) を廃止したが、
 * Sentry には URL がイベントの各所 (request.url / breadcrumb / transaction 名) に
 * 入るため、万一残った経路や外部サービスのコールバック URL に含まれる
 * 資格情報がエラートラッカーへ蓄積されないよう、送信前に必ずマスクする。
 */
const SENSITIVE_QUERY_KEYS = ['token', 'access_token', 'id_token', 'authorization', 'code', 'ticket', 'state'];

function maskUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, window.location.origin);
    let changed = false;
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, '[Filtered]');
        changed = true;
      }
    }
    return changed ? url.toString() : rawUrl;
  } catch {
    return rawUrl;
  }
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
    sendDefaultPii: false,
    // ブラウザ拡張機能や一時的なネットワーク断など、アプリ側で対処不能なノイズを抑制する
    ignoreErrors: [
      // Safari 拡張機能の注入スクリプト由来 (自前バンドルには存在しない変数)
      "Can't find variable: EmptyRanges",
      // ページ遷移・タブクローズ・回線瞬断で fetch が中断したときの一時的エラー
      'Network Error',
      'Load failed',
      'Failed to fetch',
    ],
    denyUrls: [
      // ブラウザ拡張機能が注入したスクリプト由来の例外を除外
      /^safari-(web-)?extension:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    // CR-1: URL とパンくずに含まれるトークン類を送信前にマスクする
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = maskUrl(event.request.url);
      }
      if (event.request?.headers) {
        for (const header of ['Authorization', 'authorization', 'Cookie', 'cookie']) {
          if (header in event.request.headers) {
            event.request.headers[header] = '[Filtered]';
          }
        }
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((crumb) => {
          const data = crumb.data;
          if (data && typeof data.url === 'string') {
            return { ...crumb, data: { ...data, url: maskUrl(data.url) } };
          }
          return crumb;
        });
      }
      return event;
    },
    beforeBreadcrumb(crumb) {
      const data = crumb.data;
      if (data && typeof data.url === 'string') {
        return { ...crumb, data: { ...data, url: maskUrl(data.url) } };
      }
      return crumb;
    },
  });
}

applyBrandTheme();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('#root コンテナが見つかりません。Flask テンプレートを確認してください。');
}

createRoot(rootElement).render(
  <StrictMode>
    <ToastProvider>
      <App />
    </ToastProvider>
  </StrictMode>,
);
